import { useEffect, useState } from 'react'
import Modal from 'react-modal'
import { Link } from 'react-router-dom'
import { AdminRefundRequestDto, ApiError } from '@ivy-monorepo/shared'
import { BaseModalProps } from '../../types/modal'
import { adminService } from '../../services/admin.service'
import { formatTicketPrice } from '../../utils/ticketPrice'
import RefundStatusTag from '../RefundStatusTag'

type Decision = 'approve' | 'reject'

type RefundRequestDetailModalProps = BaseModalProps & {
  refundRequestPublicId: string | null
  /** Called after an approve / decline so the list can refresh. */
  onChanged: () => void
  onOpenFeedback: (commentPublicId: string) => void
}

const formatDate = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleString() : '—')

/**
 * A refund request with the purchase it is for. Approve refunds through Square (and posts the
 * decision in the request's feedback topic); Decline only posts the decision. The note, when given,
 * is what the user reads in that topic.
 */
const RefundRequestDetailModal = ({
  isOpen,
  onClose,
  refundRequestPublicId,
  onChanged,
  onOpenFeedback,
}: RefundRequestDetailModalProps) => {
  const [request, setRequest] = useState<AdminRefundRequestDto | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [decision, setDecision] = useState<Decision | null>(null)
  const [note, setNote] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)
  const [isConfirmingRefund, setIsConfirmingRefund] = useState(false)

  useEffect(() => {
    if (!isOpen || !refundRequestPublicId) return
    setRequest(null)
    setLoadError(null)
    setDecision(null)
    setNote('')
    setActionError(null)
    setIsConfirmingRefund(false)
    adminService
      .getRefundRequest(refundRequestPublicId)
      .then(setRequest)
      .catch((err: ApiError) => setLoadError(err.friendlyMessage || err.message || 'Failed to load the refund request.'))
  }, [isOpen, refundRequestPublicId])

  const status = request?.status
  // PROCESSING without a Square refund ID: the refund call never answered — Approve retries it safely.
  const canApprove =
    status === 'PENDING' || status === 'FAILED' || (status === 'PROCESSING' && !request?.squareRefundId)
  const canReject = status === 'PENDING' || status === 'FAILED'

  const submitDecision = async () => {
    if (!request || !decision) return
    setIsConfirmingRefund(false)
    setIsSubmitting(true)
    setActionError(null)
    try {
      const trimmed = note.trim() || undefined
      const updated =
        decision === 'approve'
          ? await adminService.approveRefundRequest(request.publicId, trimmed)
          : await adminService.rejectRefundRequest(request.publicId, trimmed)
      setRequest(updated)
      setDecision(null)
      setNote('')
      onChanged()
    } catch (err) {
      const apiError = err as ApiError
      setActionError(apiError.friendlyMessage || apiError.message || 'Something went wrong.')
      // The request may have changed (e.g. marked FAILED) — reload it.
      adminService.getRefundRequest(request.publicId).then(setRequest).catch(() => undefined)
      onChanged()
    } finally {
      setIsSubmitting(false)
    }
  }

  const ticket = request?.ticket
  const remainingCents = ticket ? ticket.totalCents - ticket.refundedCents : 0

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      ariaHideApp={false}
      className="modal-popup refund-request-modal"
      overlayClassName="modal-overlay"
      contentLabel="Refund request"
    >
      <div className="modal-content-wrapper">
        <div className="modal-body-content">
          {loadError && <div className="page-error">{loadError}</div>}
          {!request && !loadError && <div className="page-loading">Loading…</div>}
          {request && ticket && (
            <>
              <div className="refund-request-header">
                <h3>Refund request · {formatTicketPrice(request.amountCents)}</h3>
                <RefundStatusTag status={request.status} />
              </div>

              <section className="refund-request-section">
                <h4>Request</h4>
                <dl>
                  <div>
                    <dt>Requested by</dt>
                    <dd>
                      {request.user.publicId ? (
                        <Link to={`/admin/users/${request.user.publicId}`}>
                          {request.user.firstName} {request.user.lastName}
                        </Link>
                      ) : (
                        `${request.user.firstName} ${request.user.lastName}`
                      )}
                      {request.user.email ? ` · ${request.user.email}` : ''}
                      {request.user.telephone ? ` · ${request.user.telephone}` : ''}
                    </dd>
                  </div>
                  <div>
                    <dt>Requested</dt>
                    <dd>{formatDate(request.createdAt)}</dd>
                  </div>
                  <div>
                    <dt>Reason</dt>
                    <dd className="refund-request-reason">{request.reason || '—'}</dd>
                  </div>
                  {request.reviewedAt && (
                    <div>
                      <dt>Decided</dt>
                      <dd>
                        {formatDate(request.reviewedAt)}
                        {request.reviewedByName ? ` by ${request.reviewedByName}` : ''}
                      </dd>
                    </div>
                  )}
                  {request.decisionNote && (
                    <div>
                      <dt>Note to user</dt>
                      <dd className="refund-request-reason">{request.decisionNote}</dd>
                    </div>
                  )}
                  {request.failureCode && (
                    <div>
                      <dt>Failure</dt>
                      <dd className="refund-request-failure">{request.failureCode}</dd>
                    </div>
                  )}
                  {request.squareRefundId && (
                    <div>
                      <dt>Square refund</dt>
                      <dd>
                        {request.squareRefundId}
                        {request.squareRefundStatus ? ` (${request.squareRefundStatus})` : ''}
                      </dd>
                    </div>
                  )}
                </dl>
              </section>

              <section className="refund-request-section">
                <h4>What was bought</h4>
                <dl>
                  <div>
                    <dt>Event</dt>
                    <dd>
                      {ticket.eventName}
                      {ticket.eventStartDate ? ` · ${formatDate(ticket.eventStartDate)}` : ''}
                    </dd>
                  </div>
                  <div>
                    <dt>Venue</dt>
                    <dd>
                      {ticket.venueName}
                      {ticket.venueAddress ? ` · ${ticket.venueAddress}` : ''}
                    </dd>
                  </div>
                  <div>
                    <dt>Tickets</dt>
                    <dd>
                      {ticket.quantity} × {formatTicketPrice(ticket.priceCents)} = {formatTicketPrice(ticket.totalCents)}
                    </dd>
                  </div>
                  <div>
                    <dt>Already refunded</dt>
                    <dd>
                      {formatTicketPrice(ticket.refundedCents)} (remaining {formatTicketPrice(remainingCents)})
                    </dd>
                  </div>
                  <div>
                    <dt>Ticket status</dt>
                    <dd>{ticket.status}</dd>
                  </div>
                  <div>
                    <dt>Paid with</dt>
                    <dd>{ticket.cardLast4 ? `${ticket.cardBrand ?? 'Card'} •••• ${ticket.cardLast4}` : '—'}</dd>
                  </div>
                  <div>
                    <dt>Purchased</dt>
                    <dd>{formatDate(ticket.purchasedAt)}</dd>
                  </div>
                  <div>
                    <dt>Square payment</dt>
                    <dd>
                      {ticket.squarePaymentId ?? '—'}
                      {ticket.receiptUrl ? (
                        <>
                          {' · '}
                          <a href={ticket.receiptUrl} target="_blank" rel="noreferrer">
                            Receipt
                          </a>
                        </>
                      ) : null}
                    </dd>
                  </div>
                </dl>
              </section>

              {decision && (
                <section className="refund-request-section">
                  <h4>{decision === 'approve' ? `Refund ${formatTicketPrice(request.amountCents)}?` : 'Decline this refund?'}</h4>
                  <p className="refund-request-hint">
                    {decision === 'approve'
                      ? 'The card is refunded through Square right away. The user gets your note (or a default message) in the feedback thread.'
                      : 'Nothing is refunded. The user gets your note (or a default message) in the feedback thread.'}
                  </p>
                  <div className="form-group">
                    <label>Note to the user (optional)</label>
                    <textarea
                      maxLength={1000}
                      value={note}
                      onChange={e => setNote(e.target.value)}
                      disabled={isSubmitting}
                      rows={3}
                    />
                  </div>
                </section>
              )}

              {actionError && <div className="page-error refund-request-action-error">{actionError}</div>}
            </>
          )}
        </div>

        <div className="modal-footer-content">
          {request?.commentPublicId && (
            <button
              type="button"
              className="styled-button refund-request-feedback-link"
              onClick={() => onOpenFeedback(request.commentPublicId)}
            >
              Open feedback thread
            </button>
          )}
          {decision ? (
            <>
              <button type="button" className="styled-button" onClick={() => setDecision(null)} disabled={isSubmitting}>
                Back
              </button>
              <button
                type="button"
                className="styled-button"
                // Refunds move money — ask once more before calling Square.
                onClick={decision === 'approve' ? () => setIsConfirmingRefund(true) : submitDecision}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Working…' : decision === 'approve' ? 'Confirm refund' : 'Confirm decline'}
              </button>
            </>
          ) : (
            <>
              <button type="button" className="styled-button" onClick={onClose}>
                Close
              </button>
              {canReject && (
                <button type="button" className="styled-button" onClick={() => setDecision('reject')}>
                  Decline
                </button>
              )}
              {canApprove && (
                <button type="button" className="styled-button" onClick={() => setDecision('approve')}>
                  {status === 'PENDING' ? 'Approve refund' : 'Retry refund'}
                </button>
              )}
            </>
          )}
        </div>
      </div>

      <Modal
        isOpen={isConfirmingRefund && !!request}
        onRequestClose={() => setIsConfirmingRefund(false)}
        ariaHideApp={false}
        className="modal-popup refund-confirm-modal"
        overlayClassName="modal-overlay"
        contentLabel="Confirm refund"
      >
        {request && (
          <div className="modal-content-wrapper">
            <h2>Refund {formatTicketPrice(request.amountCents)}?</h2>
            <p className="refund-request-hint">
              Are you sure you want to refund <strong>{formatTicketPrice(request.amountCents)}</strong> to{' '}
              <strong>
                {request.user.firstName} {request.user.lastName}
              </strong>
              ? The money goes back to their card through Square and this can't be undone.
            </p>
            <div className="modal-footer-content">
              <button type="button" className="styled-button" onClick={() => setIsConfirmingRefund(false)}>
                Cancel
              </button>
              <button type="button" className="styled-button danger" onClick={submitDecision}>
                Yes, refund
              </button>
            </div>
          </div>
        )}
      </Modal>
    </Modal>
  )
}

export default RefundRequestDetailModal
